import React, { useState } from 'react';

// Card de resultado do Agente (§16 — "cards de resultado", não só texto
// corrido). O backend manda o payload de verdade da última tool bem-sucedida
// (`agente/agent_service.py: dados_resposta`), e este componente decide como
// exibir.
//
// Reescrito depois do feedback "as respostas precisam ter mais detalhes e ser
// clicáveis". A versão anterior era genérica demais e perdia informação que já
// estava no payload: escolhia UM rótulo e UM valor por linha varrendo duas
// listas de nomes preferidos, então uma lista de contas virava "descrição +
// saldo" — sem vencimento, sem status. E descartava todo campo `_id` (pra não
// mostrar UUID), o que também jogava fora a única coisa capaz de virar um link.
//
// O desenho agora separa as duas preocupações que estavam misturadas:
//   1. COLUNAS — cada tipo de resultado declara o que importa nele (COLUNAS_POR_CHAVE).
//      Continua havendo fallback genérico pra tool que não tem esquema.
//   2. IDENTIDADE — o id nunca é RENDERIZADO, mas é preservado pra navegação.
//      "Não mostrar UUID" nunca significou "jogar o id fora".

// ── Formatação ────────────────────────────────────────────────────────────
// Valores monetários chegam como string decimal ("300.00") — o backend usa
// Decimal e serializa como texto de propósito, pra não passar por float. Aqui
// vira R$ com separador brasileiro; antes aparecia "300.00" cru na tela.

function formatarMoeda(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatarData(v) {
  if (!v) return '—';
  // Datas vêm ISO ("2026-02-15" ou com hora). Corta no T e monta dd/mm/aaaa
  // sem `new Date()`, que reinterpretaria a string em UTC e podia exibir o
  // dia anterior dependendo do fuso do navegador.
  const [ymd] = String(v).split('T');
  const partes = ymd.split('-');
  if (partes.length !== 3) return String(v);
  const [ano, mes, dia] = partes;
  return `${dia}/${mes}/${ano}`;
}

function formatarTexto(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (Array.isArray(v)) return `${v.length} item(ns)`;
  if (typeof v === 'object') return '—';
  return String(v);
}

// Rótulos legíveis pros status/situações que o backend grava em CAIXA ALTA.
const ROTULO_SITUACAO = {
  PENDENTE: 'Pendente',
  PARCIAL: 'Parcial',
  PAGO: 'Pago',
  CANCELADO: 'Cancelado',
  VENCIDO: 'Vencido',
  PARCIAL_VENCIDO: 'Parcial vencido',
  DISPONIVEL: 'Disponível',
  EM_USO: 'Em uso',
  INSTALADO: 'Instalado',
  EM_MANUTENCAO: 'Em manutenção',
  DEFEITUOSO: 'Defeituoso',
  BAIXADO: 'Baixado',
  ATIVO: 'Ativo',
  ARQUIVADO: 'Arquivado',
  RASCUNHO: 'Rascunho',
};

// Situações que merecem destaque visual — o que exige atenção precisa ser
// reconhecível sem ler o texto todo.
const TOM_SITUACAO = {
  VENCIDO: 'ruim',
  PARCIAL_VENCIDO: 'ruim',
  DEFEITUOSO: 'ruim',
  CANCELADO: 'neutro',
  BAIXADO: 'neutro',
  ARQUIVADO: 'neutro',
  PAGO: 'bom',
  DISPONIVEL: 'bom',
  ATIVO: 'bom',
  PENDENTE: 'atencao',
  PARCIAL: 'atencao',
  EM_MANUTENCAO: 'atencao',
};

function Selo({ valor }) {
  if (!valor) return <>—</>;
  const tom = TOM_SITUACAO[valor] || 'neutro';
  return <span className={`crm-agent-selo crm-agent-selo-${tom}`}>{ROTULO_SITUACAO[valor] || valor}</span>;
}

// ── Esquema de colunas por tipo de lista ──────────────────────────────────
// A chave é o nome do array no payload da tool (ex.: `contas`, `ativos`), não
// o nome da tool: várias tools diferentes devolvem `contas` com o mesmo shape,
// então o esquema é reaproveitado sem precisar mapear tool a tool.
//
// `campo` é lido do item; `tipo` decide a formatação; `alvo` liga a linha a
// uma tela do CRM (ver ENTIDADE_PARA_NAVEGACAO).

// Contas a receber saem de `receivable_service.listar` sob TRÊS nomes
// diferentes conforme a tool: `contas` (resumo financeiro do cliente),
// `receitas` (busca e vencidas) e `despesas` (contas a pagar, mesmo shape
// vindo de `payable_service`). Todas compartilham o mesmo esquema — daí
// `COLUNAS_CONTA` ser reaproveitada em vez de repetida: esquecer um desses
// nomes faz a lista cair no fallback genérico e perder vencimento/situação,
// que foi exatamente a queixa que originou esta reescrita.
const COLUNAS_CONTA = {
  entidade: 'conta',
  colunas: [
    { campo: 'descricao', titulo: 'Descrição', tipo: 'texto', principal: true },
    { campo: 'cliente_nome', titulo: 'Cliente', tipo: 'texto', opcional: true },
    { campo: 'vencimento', titulo: 'Vencimento', tipo: 'data' },
    { campo: 'situacao', titulo: 'Situação', tipo: 'selo' },
    { campo: 'saldo', titulo: 'Saldo', tipo: 'moeda', numerica: true },
  ],
};

const COLUNAS_POR_CHAVE = {
  contas: COLUNAS_CONTA,
  receitas: COLUNAS_CONTA,
  despesas: {
    // Despesa não navega para Financeiro por cliente (o vínculo é
    // fornecedor, não cliente) — por isso sem `entidade`: a linha não fica
    // clicável em vez de levar a uma tela sem relação.
    colunas: [
      { campo: 'descricao', titulo: 'Descrição', tipo: 'texto', principal: true },
      { campo: 'fornecedor_nome', titulo: 'Fornecedor', tipo: 'texto', opcional: true },
      { campo: 'vencimento', titulo: 'Vencimento', tipo: 'data' },
      { campo: 'situacao', titulo: 'Situação', tipo: 'selo' },
      { campo: 'saldo', titulo: 'Saldo', tipo: 'moeda', numerica: true },
    ],
  },
  clientes: {
    entidade: 'cliente',
    colunas: [
      { campo: 'nome', titulo: 'Cliente', tipo: 'texto', principal: true },
      { campo: 'documento', titulo: 'Documento', tipo: 'texto', opcional: true },
      { campo: 'status', titulo: 'Status', tipo: 'selo' },
    ],
  },
  contatos: {
    colunas: [
      { campo: 'nome', titulo: 'Contato', tipo: 'texto', principal: true },
      { campo: 'cargo', titulo: 'Cargo', tipo: 'texto', opcional: true },
      { campo: 'telefone', titulo: 'Telefone', tipo: 'texto' },
      { campo: 'email', titulo: 'E-mail', tipo: 'texto', opcional: true },
    ],
  },
  projetos: {
    entidade: 'projeto',
    colunas: [
      { campo: 'nome', titulo: 'Projeto', tipo: 'texto', principal: true },
      { campo: 'cliente_nome', titulo: 'Cliente', tipo: 'texto', opcional: true },
      { campo: 'status', titulo: 'Status', tipo: 'selo' },
    ],
  },
  tarefas: {
    colunas: [
      { campo: 'titulo', titulo: 'Tarefa', tipo: 'texto', principal: true },
      { campo: 'responsavel_nome', titulo: 'Responsável', tipo: 'texto', opcional: true },
      { campo: 'prazo', titulo: 'Prazo', tipo: 'data', opcional: true },
      { campo: 'status', titulo: 'Status', tipo: 'selo' },
    ],
  },
  produtos: {
    colunas: [
      { campo: 'nome', titulo: 'Produto', tipo: 'texto', principal: true },
      { campo: 'sku', titulo: 'SKU', tipo: 'texto', opcional: true },
      { campo: 'saldo_atual', titulo: 'Saldo', tipo: 'texto', numerica: true },
      { campo: 'estoque_minimo', titulo: 'Mínimo', tipo: 'texto', numerica: true, opcional: true },
    ],
  },
  ativos: {
    entidade: 'ativo',
    colunas: [
      { campo: 'asset_tag', titulo: 'Etiqueta', tipo: 'texto', principal: true },
      { campo: 'produto_nome', titulo: 'Produto', tipo: 'texto' },
      { campo: 'cliente_nome', titulo: 'Com', tipo: 'texto', opcional: true },
      { campo: 'localizacao', titulo: 'Local', tipo: 'texto', opcional: true },
      { campo: 'status', titulo: 'Status', tipo: 'selo' },
    ],
  },
  documentos: {
    entidade: 'documento',
    colunas: [
      { campo: 'titulo', titulo: 'Documento', tipo: 'texto', principal: true },
      { campo: 'tipo', titulo: 'Tipo', tipo: 'texto', opcional: true },
      { campo: 'versao_atual', titulo: 'Versão', tipo: 'texto', numerica: true, opcional: true },
      { campo: 'status', titulo: 'Status', tipo: 'selo' },
    ],
  },
  assinaturas: {
    colunas: [
      { campo: 'descricao', titulo: 'Assinatura', tipo: 'texto', principal: true },
      { campo: 'cliente_nome', titulo: 'Cliente', tipo: 'texto', opcional: true },
      { campo: 'valor', titulo: 'Valor', tipo: 'moeda', numerica: true },
      { campo: 'status', titulo: 'Status', tipo: 'selo' },
    ],
  },
  destinatarios: {
    colunas: [
      { campo: 'nome', titulo: 'Destinatário', tipo: 'texto', principal: true },
      { campo: 'telefone', titulo: 'Telefone', tipo: 'texto' },
    ],
  },
  itens: {
    colunas: [
      { campo: 'nome', titulo: 'Item', tipo: 'texto', principal: true },
      { campo: 'telefone', titulo: 'Telefone', tipo: 'texto', opcional: true },
      { campo: 'texto', titulo: 'Mensagem', tipo: 'texto', opcional: true },
    ],
  },
};

// Um clique na linha precisa virar uma tela do CRM. O `CrmShell` navega por
// `navegar(destino, contexto)`, e cada tela só aceita as chaves de contexto
// que ela declara receber — ver a montagem dos módulos no CrmShell:
//   clientes → clienteId · projetos → projetoId · patrimonio → ativoId
//   documentos → documentoId · financeiro → clienteIdFinanceiro
//
// Recebe o ITEM inteiro, não só o id, porque nem toda entidade navega pelo
// próprio id: `financeiro` não abre uma conta individual (não existe essa
// chave de contexto hoje), então uma conta navega pelo CLIENTE dela, que é a
// tela onde ela aparece. Devolver `null` marca a linha como não-clicável — é
// preferível a um clique que troca de tela e não mostra nada relacionado.
const ENTIDADE_PARA_NAVEGACAO = {
  cliente:   (item) => (item.id ? ['clientes', { clienteId: item.id }] : null),
  projeto:   (item) => (item.id ? ['projetos', { projetoId: item.id }] : null),
  ativo:     (item) => (item.id ? ['patrimonio', { ativoId: item.id }] : null),
  documento: (item) => (item.id ? ['documentos', { documentoId: item.id }] : null),
  conta:     (item) => (item.cliente_id ? ['financeiro', { clienteIdFinanceiro: item.cliente_id }] : null),
};

// ── Fallback genérico (tool sem esquema declarado) ────────────────────────
const CHAVES_ARRAY_PREFERIDAS = Object.keys(COLUNAS_POR_CHAVE);
const CHAVES_LABEL_PREFERIDAS = ['nome', 'cliente_nome', 'descricao', 'produto_nome', 'contato_nome', 'projeto_nome', 'titulo', 'asset_tag'];

function ehIdOuTecnico(chave) {
  return chave === 'id' || chave.endsWith('_id') || chave === 'waha_id' || chave === 'tenant_id';
}

function esquemaGenerico(itens) {
  const amostra = itens[0] || {};
  const principal = CHAVES_LABEL_PREFERIDAS.find((c) => c in amostra) || Object.keys(amostra).find((c) => !ehIdOuTecnico(c));
  const outras = Object.keys(amostra)
    .filter((c) => c !== principal && !ehIdOuTecnico(c))
    .filter((c) => { const v = amostra[c]; return v === null || typeof v !== 'object'; })
    .slice(0, 3);
  return {
    colunas: [
      { campo: principal, titulo: 'Item', tipo: 'texto', principal: true },
      ...outras.map((c) => ({ campo: c, titulo: c.replace(/_/g, ' '), tipo: 'auto' })),
    ],
  };
}

function celula(item, col) {
  const v = item[col.campo];
  if (col.tipo === 'moeda') return formatarMoeda(v);
  if (col.tipo === 'data') return formatarData(v);
  if (col.tipo === 'selo') return <Selo valor={v} />;
  if (col.tipo === 'auto') {
    // Heurística só pro fallback: nome de campo indica como formatar.
    if (/valor|saldo|custo|preco/.test(col.campo)) return formatarMoeda(v);
    if (/venc|data|prazo|_em$/.test(col.campo)) return formatarData(v);
    if (/status|situacao/.test(col.campo)) return <Selo valor={v} />;
  }
  return formatarTexto(v);
}

function encontrarArrayPrincipal(resultado) {
  for (const chave of CHAVES_ARRAY_PREFERIDAS) {
    const v = resultado[chave];
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') return { chave, itens: v };
  }
  for (const [chave, v] of Object.entries(resultado)) {
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') return { chave, itens: v };
  }
  return null;
}

// ── Tabela ────────────────────────────────────────────────────────────────

const LINHAS_INICIAIS = 8;

function TabelaResultado({ chave, itens, onNavegar }) {
  const [expandido, setExpandido] = useState(false);

  const esquema = COLUNAS_POR_CHAVE[chave] || esquemaGenerico(itens);
  // Coluna opcional some quando NENHUM item preencheu — evita uma coluna
  // inteira de "—" (ex.: "Cliente" numa busca já filtrada por um cliente só).
  const colunas = esquema.colunas.filter(
    (c) => c.campo && (!c.opcional || itens.some((i) => i[c.campo] !== null && i[c.campo] !== undefined && i[c.campo] !== '')),
  );

  const navegacao = esquema.entidade ? ENTIDADE_PARA_NAVEGACAO[esquema.entidade] : null;
  const podeNavegar = Boolean(navegacao && onNavegar);
  // Resolve item a item: numa mesma lista pode haver linha navegável e linha
  // sem destino (ex.: conta a receber sem cliente vinculado).
  const destinoDe = (item) => (podeNavegar ? navegacao(item) : null);

  const visiveis = expandido ? itens : itens.slice(0, LINHAS_INICIAIS);
  const restantes = itens.length - visiveis.length;

  const abrir = (item) => {
    const destino = destinoDe(item);
    if (!destino) return;
    onNavegar(destino[0], destino[1]);
  };

  return (
    <>
      <div className="crm-agent-card-scroll">
        <table className="crm-agent-card-table">
          <thead>
            <tr>
              {colunas.map((c) => (
                <th key={c.campo} className={c.numerica ? 'crm-agent-col-num' : undefined}>{c.titulo}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visiveis.map((item, i) => {
              const linhaClicavel = Boolean(destinoDe(item));
              return (
                <tr
                  key={item.id || i}
                  className={linhaClicavel ? 'crm-agent-linha-clicavel' : undefined}
                  onClick={linhaClicavel ? () => abrir(item) : undefined}
                  onKeyDown={linhaClicavel ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(item); } } : undefined}
                  tabIndex={linhaClicavel ? 0 : undefined}
                  role={linhaClicavel ? 'button' : undefined}
                  title={linhaClicavel ? 'Abrir no CRM' : undefined}
                >
                  {colunas.map((c) => (
                    <td
                      key={c.campo}
                      className={[
                        c.numerica ? 'crm-agent-col-num' : '',
                        c.principal ? 'crm-agent-col-principal' : '',
                      ].filter(Boolean).join(' ') || undefined}
                    >
                      {celula(item, c)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {restantes > 0 && (
        <button type="button" className="crm-agent-card-more" onClick={() => setExpandido(true)}>
          Ver mais {restantes}
        </button>
      )}
      {expandido && itens.length > LINHAS_INICIAIS && (
        <button type="button" className="crm-agent-card-more" onClick={() => setExpandido(false)}>
          Recolher
        </button>
      )}
    </>
  );
}

// ── Ficha (resultado que não é lista) ─────────────────────────────────────

// Campos de resumo que ganham formatação de moeda mesmo fora de uma lista.
const CAMPOS_MOEDA = /valor|saldo|total_pago|custo|preco|faturamento/;
const CAMPOS_DATA = /venc|data|prazo|_em$/;

const ROTULOS_CAMPO = {
  saldo_total_em_aberto: 'Saldo total em aberto',
  saldo_vencido: 'Saldo vencido',
  quantidade_contas_em_aberto: 'Contas em aberto',
  quantidade_contas_vencidas: 'Contas vencidas',
  cliente_nome: 'Cliente',
  projeto_nome: 'Projeto',
  produto_nome: 'Produto',
  total: 'Total',
};

function FichaResultado({ resultado }) {
  const linhas = Object.entries(resultado).filter(
    ([chave, v]) => !ehIdOuTecnico(chave) && !(Array.isArray(v) && v.length && typeof v[0] === 'object') && v !== null && v !== undefined && v !== '',
  );
  if (linhas.length === 0) return null;

  return (
    <div>
      {linhas.slice(0, 10).map(([chave, v]) => {
        let exibicao;
        if (typeof v === 'object' && !Array.isArray(v)) {
          // Sub-objeto (ex.: `resumo`): achata em "a: x · b: y" em vez de
          // despejar JSON cru, que era o que a versão anterior fazia.
          exibicao = Object.entries(v)
            .map(([k, val]) => `${k.replace(/_/g, ' ')}: ${CAMPOS_MOEDA.test(k) ? formatarMoeda(val) : formatarTexto(val)}`)
            .join(' · ');
        } else if (CAMPOS_MOEDA.test(chave)) {
          exibicao = formatarMoeda(v);
        } else if (CAMPOS_DATA.test(chave)) {
          exibicao = formatarData(v);
        } else if (/status|situacao/.test(chave)) {
          exibicao = <Selo valor={v} />;
        } else {
          exibicao = formatarTexto(v);
        }
        return (
          <div className="crm-agent-card-row" key={chave}>
            <span className="crm-agent-card-row-label">{ROTULOS_CAMPO[chave] || chave.replace(/_/g, ' ')}</span>
            <span className="crm-agent-card-row-value">{exibicao}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────

const ROTULOS_TOOL = {
  search_clients: 'Clientes encontrados',
  get_client: 'Cliente',
  get_client_contacts: 'Contatos',
  get_client_financial_summary: 'Resumo financeiro do cliente',
  get_financial_summary: 'Resumo financeiro',
  search_receivables: 'Contas a receber',
  search_overdue_receivables: 'Contas vencidas',
  get_receivable: 'Conta a receber',
  create_receivable: 'Conta a receber criada',
  update_receivable: 'Conta a receber atualizada',
  link_receivable_to_project: 'Vínculo com projeto',
  register_payment: 'Pagamento registrado',
  search_subscriptions: 'Assinaturas',
  search_projects: 'Projetos encontrados',
  get_project: 'Projeto',
  get_project_summary: 'Resumo do projeto',
  create_project_task: 'Tarefa criada',
  update_project_task: 'Tarefa atualizada',
  search_products: 'Produtos encontrados',
  get_stock_summary: 'Resumo de estoque',
  get_low_stock_products: 'Produtos com estoque baixo',
  search_assets: 'Equipamentos encontrados',
  get_asset: 'Equipamento',
  get_patrimonio_summary: 'Resumo do patrimônio',
  search_documents: 'Documentos encontrados',
  get_document: 'Documento',
  find_whatsapp_recipient: 'Destinatário',
  get_whatsapp_status: 'Status do WhatsApp',
  send_whatsapp_message: 'Envio de WhatsApp',
  prepare_whatsapp_bulk_send: 'Lote de WhatsApp preparado',
  send_whatsapp_group_message: 'Mensagem para o grupo',
  check_whatsapp_number: 'Número verificado',
};

export function AgentResultCard({ tool, resultado, onNavegar }) {
  if (!resultado || typeof resultado !== 'object') return null;
  if (resultado.erro) return null; // erros já aparecem no texto da resposta

  const titulo = ROTULOS_TOOL[tool] || tool;
  const arrayPrincipal = encontrarArrayPrincipal(resultado);
  const contagem = typeof resultado.total === 'number'
    ? resultado.total
    : arrayPrincipal ? arrayPrincipal.itens.length : null;

  return (
    <div className="crm-agent-card">
      <div className="crm-agent-card-title">
        {titulo}
        {contagem !== null && <span className="crm-agent-card-count">{contagem}</span>}
      </div>
      {arrayPrincipal
        ? <TabelaResultado chave={arrayPrincipal.chave} itens={arrayPrincipal.itens} onNavegar={onNavegar} />
        : <FichaResultado resultado={resultado} />}
    </div>
  );
}

export default AgentResultCard;
